import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../App";
import { Users, CheckCircle, AlertTriangle, Calendar, MapPin, TrendingUp, Award } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Performance() {
  const { token, user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30");

  useEffect(() => {
    fetchDashboard();
  }, [period]);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/performance/dashboard?days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error("Error fetching performance:", error);
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== "founder") {
    return (
      <div className="p-6 text-center">
        <p className="text-[#888888]">Performance metrics are only available for founders.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[#262626] rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-32 bg-[#262626] rounded"></div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="performance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Team Performance</h1>
          <p className="text-[#888888] text-sm mt-1">Monitor associate manager productivity and metrics</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40 bg-[#1A1A1A] border-[#262626]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#1A1A1A] border-[#262626]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#888888] text-xs uppercase tracking-wider">Associates</p>
                <p className="text-3xl font-bold text-white mt-1">{dashboard?.total_associates || 0}</p>
              </div>
              <Users className="h-8 w-8 text-[#FF6B35]" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1A1A1A] border-[#262626]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#888888] text-xs uppercase tracking-wider">Tasks Done</p>
                <p className="text-3xl font-bold text-[#4CAF50] mt-1">{dashboard?.total_tasks_completed || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-[#4CAF50]" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1A1A1A] border-[#262626]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#888888] text-xs uppercase tracking-wider">Overdue</p>
                <p className="text-3xl font-bold text-[#FF4444] mt-1">{dashboard?.total_tasks_overdue || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-[#FF4444]" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1A1A1A] border-[#262626]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#888888] text-xs uppercase tracking-wider">Completion Rate</p>
                <p className="text-3xl font-bold text-white mt-1">{dashboard?.overall_completion_rate || 0}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-[#2196F3]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Performer */}
      {dashboard?.top_performer && (
        <Card className="bg-gradient-to-r from-[#1A1A1A] to-[#262626] border-[#FF6B35]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-[#FF6B35]/20 flex items-center justify-center">
                <Award className="h-7 w-7 text-[#FF6B35]" />
              </div>
              <div>
                <p className="text-[#FF6B35] text-xs uppercase tracking-wider font-medium">Top Performer</p>
                <p className="text-xl font-semibold text-white">{dashboard.top_performer.user_name}</p>
                <p className="text-[#888888] text-sm">{dashboard.top_performer.tasks_completed} tasks completed this period</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Associate Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {dashboard?.associates?.map((associate, index) => (
          <Card key={associate.user_id + index} className="bg-[#1A1A1A] border-[#262626]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-white">{associate.user_name}</CardTitle>
                  <p className="text-[#888888] text-sm">{associate.user_email}</p>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  associate.completion_rate >= 80 ? 'bg-[#4CAF50]/20 text-[#4CAF50]' :
                  associate.completion_rate >= 50 ? 'bg-[#FF6B35]/20 text-[#FF6B35]' :
                  'bg-[#FF4444]/20 text-[#FF4444]'
                }`}>
                  {associate.completion_rate}% Rate
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center p-3 bg-[#111111] rounded">
                  <p className="text-2xl font-bold text-[#4CAF50]">{associate.tasks_completed}</p>
                  <p className="text-[#888888] text-xs mt-1">Completed</p>
                </div>
                <div className="text-center p-3 bg-[#111111] rounded">
                  <p className="text-2xl font-bold text-[#FF4444]">{associate.tasks_overdue}</p>
                  <p className="text-[#888888] text-xs mt-1">Overdue</p>
                </div>
                <div className="text-center p-3 bg-[#111111] rounded">
                  <p className="text-2xl font-bold text-white">{associate.avg_task_completion_days}</p>
                  <p className="text-[#888888] text-xs mt-1">Avg Days</p>
                </div>
              </div>
              
              <div className="flex justify-between mt-4 pt-4 border-t border-[#262626]">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-[#888888]" />
                  <span className="text-[#888888]">{associate.meetings_scheduled} meetings</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-[#888888]" />
                  <span className="text-[#888888]">{associate.site_visits_completed} visits</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
